/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { HoverEffectWrapper } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';

import { Link, Typography2 } from '@coursera/cds-core';
import { PlayIcon } from '@coursera/cds-icons';
import type { SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';
import type { Degree_WebinarFragment as DegreeWebinar } from 'bundles/premium-hub/queries/__generated__/getDegreeWebinars';
import { eventSchemaDataFromWebinar } from 'bundles/premium-hub/utils/structuredDataUtils';
import GoogleMarkup from 'bundles/seo/components/GoogleMarkup';

import _t from 'i18n!nls/premium-hub';

const THUMBNAIL_WIDTH = 112;
const THUMBNAIL_HEIGHT = 80;

const THUMBNAIL_IMAGE_PARAMS = {
  auto: 'format,compress',
  fit: 'crop',
  height: THUMBNAIL_HEIGHT,
  width: THUMBNAIL_WIDTH,
};

type RecordingWebinarCardProps = {
  webinar: DegreeWebinar;
  sectionName: SectionName;
};

// Matches the 11-character video id out of the YouTube URL shapes recording links use
// (youtube.com/watch?v=, youtube.com/embed/, youtu.be/, etc.), regardless of other query params.
const YOUTUBE_VIDEO_ID_REGEX =
  /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/;

export const getYoutubeThumbnailUrl = (recordingLink: string): string | undefined => {
  const videoId = recordingLink.match(YOUTUBE_VIDEO_ID_REGEX)?.[1];
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : undefined;
};

const styles = {
  card: css`
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    box-sizing: border-box;
    height: 100%;
    padding: var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-200);
    background-color: var(--cds-color-blue-25);
  `,
  thumbnail: css`
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: ${THUMBNAIL_WIDTH}px;
    height: ${THUMBNAIL_HEIGHT}px;
    border-radius: var(--cds-border-radius-100);
    background-color: var(--cds-color-neutral-background-primary-weak);
    color: var(--cds-color-neutral-primary-invert);
    overflow: hidden;
  `,
  thumbnailImage: css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  thumbnailOverlay: css`
    position: absolute;
    inset: 0;
    background-color: var(--cds-color-neutral-primary);
    opacity: 0.4;
  `,
  playCircle: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: var(--cds-color-utility-overlay);
  `,
  playIcon: css`
    width: 43px;
    height: 43px;
  `,
  details: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    min-width: 0;
  `,
  label: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  title: css`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `,
  watchLink: css`
    position: static;
    margin-top: var(--cds-spacing-50);

    &::before {
      content: '';
      position: absolute;
      inset: 0;
    }
  `,
};

export const RecordingWebinarCard = ({ webinar, sectionName }: RecordingWebinarCardProps) => {
  const track = useTracker();
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const { catalogDegree, title, recordingLink } = webinar;

  if (!recordingLink) return null;

  const languageCode = catalogDegree?.languageCode;
  const thumbnailUrl = getYoutubeThumbnailUrl(recordingLink);
  const structuredData = eventSchemaDataFromWebinar(webinar);

  if (!thumbnailUrl) {
    return null;
  }

  const onWatchButtonClick = () => {
    track('click_button', {
      button: {
        name: 'degree_webinar_watch_recording',
        linkType: 'external',
        linkURL: recordingLink,
      },
      pageSection: { sectionName, index: sectionIndex },
    });
  };

  return (
    <React.Fragment>
      {structuredData && <GoogleMarkup graph={structuredData} />}
      <HoverEffectWrapper css={styles.card} data-testid="recording-webinar-card">
        <div css={styles.thumbnail} aria-hidden="true">
          <React.Fragment>
            <Imgix
              src={thumbnailUrl}
              alt=""
              imgParams={THUMBNAIL_IMAGE_PARAMS}
              loading="lazy"
              css={styles.thumbnailImage}
            />
            <div css={styles.thumbnailOverlay} />
          </React.Fragment>
          <span css={styles.playCircle}>
            <PlayIcon size="xlarge" color="invert" css={styles.playIcon} />
          </span>
        </div>
        <div css={styles.details}>
          <Typography2 component="p" variant="bodyTertiary" css={styles.label}>
            {_t('Past recording')}
          </Typography2>
          <Typography2 component="h3" variant="bodySecondary" css={styles.title} lang={languageCode}>
            {title}
          </Typography2>
          <Link
            href={recordingLink}
            variant="standardSecondary"
            typographyVariant="bodyPrimary"
            target="_blank"
            rel="noopener noreferrer"
            hrefLang={languageCode}
            aria-label={_t('Watch a replay of #{title}', { title })}
            css={styles.watchLink}
            onClick={onWatchButtonClick}
          >
            {_t('Watch')}
          </Link>
        </div>
      </HoverEffectWrapper>
    </React.Fragment>
  );
};

export default React.memo(RecordingWebinarCard);
